import { Component, EventEmitter, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpStatusEnum } from '@core/api';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from '@shared/components/general/loader/loader.component';
import { CustomValidatorErrorEnum, ValidatorFormEntityEnum } from '@shared/enums';
import { RegexTypeEnum } from '@shared/enums/regex.enum';
import { CustomValidators } from '@shared/helpers';
import { NewInformedUserForm } from '@shared/interfaces';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { OrderSetupApi } from '../../../../features/orders/order-setup/_core/api';
import { ValidationErrorComponent } from '../../general/validation-error/validation-error.component';

@Component({
    selector: 'app-order-summary-add-new-informed-user-modal',
    standalone: true,
    imports: [
        TranslateModule,
        ButtonModule,
        DividerModule,
        ReactiveFormsModule,
        LoaderComponent,
        DialogModule,
        InputTextModule,
        ValidationErrorComponent,
    ],
    templateUrl: './order-summary-add-new-informed-user-modal.component.html',
    styleUrls: ['./order-summary-add-new-informed-user-modal.component.scss'],
})
export class OrderSummaryAddNewInformedUserModalComponent {
    @Output() isModalVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onAddNewInformedUser: EventEmitter<{ email: string; fullName: string; id: number }> = new EventEmitter<{
        email: string;
        fullName: string;
        id: number;
    }>();

    readonly newInformedUserForm = this.initializeNewInformedUserForm();
    readonly customValidatorErrors = CustomValidatorErrorEnum;
    readonly validatorFormEntities = ValidatorFormEntityEnum;

    isAddingNewInformedUser = false;

    constructor(private orderSetupApi: OrderSetupApi) {}

    initializeNewInformedUserForm(): FormGroup<NewInformedUserForm> {
        return new FormGroup({
            firstName: new FormControl('', [
                Validators.required,
                Validators.minLength(2),
                Validators.maxLength(50),
                Validators.pattern(RegexTypeEnum.ORDERS_INFORMED_USER_NAME),
                CustomValidators.emptyInputValidator,
            ]),
            lastName: new FormControl('', [
                Validators.required,
                Validators.minLength(2),
                Validators.maxLength(50),
                Validators.pattern(RegexTypeEnum.ORDERS_INFORMED_USER_NAME),
                CustomValidators.emptyInputValidator,
            ]),
            email: new FormControl('', [
                Validators.required,
                Validators.maxLength(200),
                CustomValidators.emailValidator(),
            ]),
        });
    }

    closeModal(): void {
        this.isModalVisibleChange.emit(false);
    }

    async onSave(): Promise<void> {
        if (this.newInformedUserForm.invalid) {
            this.newInformedUserForm.markAllAsTouched();
            return;
        }

        this.isAddingNewInformedUser = true;

        const response = await this.orderSetupApi.addInformedUser({
            email: this.email.value!,
            firstName: this.firstName.value!,
            lastName: this.lastName.value!,
        });

        if (response.status === HttpStatusEnum.SUCCESS && response.data) {
            this.onAddNewInformedUser.emit({
                email: this.email.value!,
                fullName: `${this.firstName.value} ${this.lastName.value}`,
                id: response.data.id,
            });
            this.closeModal();
        }

        this.isAddingNewInformedUser = false;
    }

    get firstName(): FormControl<string | null> {
        return this.newInformedUserForm.controls.firstName;
    }

    get lastName(): FormControl<string | null> {
        return this.newInformedUserForm.controls.lastName;
    }

    get email(): FormControl<string | null> {
        return this.newInformedUserForm.controls.email;
    }
}
